import React, { useLayoutEffect, useRef } from 'react';
import './UnitedPage.css';
import './PolymarketPage.css';
import PolymarketPlaceholder from './PolymarketPlaceholder';

/* Rendered inside ProjectReveal's own scroll surface, mirroring the United
   page. Reuses the United page's class vocabulary (the same way KtpPage rides
   on the mesa styles) — ProjectReveal's entrance choreography targets
   .united-text / .comparison-section, so new names here would opt out of it.
   PolymarketPage.css layers the deck-specific bits on top.

   Storyboard deck: after the intro, every section is a .pm-member layer
   stacked absolutely inside one sticky, viewport-sized .pm-stage. Scroll
   position — never a threshold or a timer — scrubs the handoff: the current
   member moves out through the top and is fully gone before the next moves
   in from the bottom — linear with the scroll, fading as it goes, never two
   sections sharing the screen — one section change per --pm-step of scroll
   (~half a screen), so moving through the deck is short work. .pm-step markers along the track are the scroll-snap seats
   (mandatory: a scroll can only end seated, never stuck mid-handoff), and
   the sticky TOC rail on the right tracks the seated member and jumps on
   click. Fully reversible: scroll back and the film runs backwards.

   Motion is applied only to the [data-pm-t] children inside each member
   (title / copy / media planes travel at slightly different depths) — never
   to the member itself, whose .comparison-section class belongs to
   ProjectReveal's open/close fades. Member-level classes (.is-off,
   .is-seated) only gate visibility and pointer-events.

   Phone-width viewports keep the deck — the splits restack copy over art
   and every scene rides 100svh (CSS) — so mobile walks the same storyboard
   as desktop. Only short viewports (≤500px — landscape phones), where a
   stacked scene can't fit, collapse to normal flow, whose tagged elements
   get a scrubbed rise instead. prefers-reduced-motion gets a plain page. */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/* Handoff motion is linear and literal: a member travels at roughly scroll
   speed (really leaving, not easing through a shuffle) while its opacity
   falls off linearly with distance. Planes differ only slightly in rate for
   depth. */
const TRAVEL_MIN = 0.88;
const TRAVEL_PLANE = 0.1;
/* The handoff is sequential, never a crossfade: a member is visible only
   within this fraction of a step around its own seat, so the outgoing one
   is fully gone (first half of the step) before the incoming one appears
   (second half) — two sections never share the screen. Kept just under 0.5
   to leave a sliver of clean cut between them. */
const VISIBLE_HALF = 0.48;
/* The flow-mode fallback (short viewports, where the deck can't stage a
   scene): each tagged element fades and rises in across its last
   MOBILE_RANGE px of approach to the viewport's bottom edge — scrubbed by
   the scroll like the deck, never triggered. Deeper planes (higher
   data-pm-t) rise a touch further, the flow echo of the deck's depth. */
const MOBILE_RANGE = 240;
const MOBILE_RISE = 34;
const MOBILE_PLANE = 0.2;

/* One entry per member, in deck order — also drives the .pm-step seat count
   (the deck height in PolymarketPage.css assumes length − 1 transitions). */
const PM_TOC: { label: string; chapter?: boolean }[] = [
  { label: 'Trader Experience', chapter: true },
  { label: 'Binary Trading' },
  { label: 'Title Cutoffs' },
  { label: 'Breathing Room' },
  { label: 'Order Controls' },
  { label: 'New Ideas', chapter: true },
  { label: 'Discovery' },
  { label: 'Reorder Feed' },
  { label: 'Leaderboard' },
];

interface PmPlane {
  el: HTMLElement;
  group: number;
  applied: number;
}

const PolymarketPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);

  const jumpTo = (k: number) => {
    const root = pageRef.current;
    if (!root) return;
    const deck = root.querySelector<HTMLElement>('.pm-deck');
    if (!deck) return;
    const surface = root.closest<HTMLElement>('.project-reveal-surface');
    const step = deck.querySelectorAll<HTMLElement>('.pm-step')[k];
    if (!step) return;
    const scrollTop = surface ? surface.scrollTop : window.scrollY;
    const target = scrollTop + step.getBoundingClientRect().top;
    (surface ?? window).scrollTo({ top: target, behavior: 'smooth' });
  };

  useLayoutEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const deck = root.querySelector<HTMLElement>('.pm-deck');
    const toc = root.querySelector<HTMLElement>('.pm-toc');
    if (!deck) return;
    const steps = Array.from(deck.querySelectorAll<HTMLElement>('.pm-step'));
    const tocItems = toc ? Array.from(toc.querySelectorAll<HTMLElement>('.pm-toc-item')) : [];
    const members = Array.from(deck.querySelectorAll<HTMLElement>('.pm-member')).map((el) => ({
      el,
      planes: Array.from(el.querySelectorAll<HTMLElement>('[data-pm-t]')).map<PmPlane>(
        (child) => ({
          el: child,
          group: Number(child.dataset.pmT) || 0,
          applied: 0,
        })
      ),
    }));
    if (members.length === 0) return;

    members.forEach(({ planes }) =>
      planes.forEach(({ el }) => {
        el.style.willChange = 'transform, opacity';
      })
    );

    /* The page scrolls inside ProjectReveal's surface, not the window. */
    const scroller: EventTarget = root.closest('.project-reveal-surface') ?? window;
    /* Must match the storyboard breakpoint in PolymarketPage.css: the deck
       runs at every width and needs only height (reduced motion is the
       early return above). */
    const deckMq = window.matchMedia('(min-height: 501px)');

    const apply = (item: PmPlane, opacity: number, ty: number) => {
      item.applied = ty;
      item.el.style.opacity = opacity > 0.999 ? '' : opacity.toFixed(3);
      item.el.style.transform =
        Math.abs(ty) > 0.1 ? `translate3d(0, ${ty.toFixed(2)}px, 0)` : '';
    };

    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      if (deckMq.matches) {
        /* Rect-based, not offsetTop: the seats sit at fractional 55vh
           multiples, and integer rounding accumulates into a visible
           off-center transform by the later seats. */
        const stepPx =
          steps.length > 1
            ? steps[1].getBoundingClientRect().top - steps[0].getBoundingClientRect().top
            : vh * 0.55;
        /* f is the continuous member index: f = k means member k is seated;
           the fraction between integers is the handoff. Clamped at the last
           seat so bottom overscroll never exits the final member. */
        const f = clamp(-deck.getBoundingClientRect().top / stepPx, -1.5, members.length - 1);
        members.forEach((m, k) => {
          const d = clamp(f - k, -1, 1);
          const a = Math.abs(d);
          m.el.classList.toggle('is-off', a >= VISIBLE_HALF);
          m.el.classList.toggle('is-seated', a < 0.4);
          for (const item of m.planes) {
            /* d > 0: past its seat, moving out the top; d < 0: on approach,
               moving in from the bottom. Linear in scroll, no easing; gone
               entirely by half a step, before the neighbour appears. */
            const ty = -d * stepPx * (TRAVEL_MIN + item.group * TRAVEL_PLANE);
            apply(item, clamp(1 - a / VISIBLE_HALF, 0, 1), ty);
          }
        });
        const idx = clamp(Math.round(f), 0, members.length - 1);
        tocItems.forEach((btn, i) => {
          btn.classList.toggle('is-current', i === idx);
          if (i === idx) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        });
        toc?.classList.toggle('is-visible', f > -0.75);
      } else {
        for (const m of members) {
          for (const item of m.planes) {
            /* Subtract our own translate so the measurement is the layout
               position, not a feedback loop. */
            const top = item.el.getBoundingClientRect().top - item.applied;
            const p = clamp((vh - top) / MOBILE_RANGE, 0, 1);
            apply(item, p, MOBILE_RISE * (1 + item.group * MOBILE_PLANE) * Math.pow(1 - p, 1.6));
          }
        }
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    const clear = () => {
      members.forEach(({ el, planes }) => {
        el.classList.remove('is-off', 'is-seated');
        planes.forEach((item) => {
          item.el.style.opacity = '';
          item.el.style.transform = '';
          item.applied = 0;
        });
      });
      tocItems.forEach((btn) => {
        btn.classList.remove('is-current');
        btn.removeAttribute('aria-current');
      });
      toc?.classList.remove('is-visible');
    };
    const onModeChange = () => {
      clear();
      schedule();
    };

    update();
    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    deckMq.addEventListener('change', onModeChange);
    /* Image loads shift layout after mount; re-derive positions when they do. */
    const ro = new ResizeObserver(schedule);
    ro.observe(root);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      scroller.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      deckMq.removeEventListener('change', onModeChange);
      ro.disconnect();
      clear();
      members.forEach(({ planes }) =>
        planes.forEach(({ el }) => {
          el.style.willChange = '';
        })
      );
    };
  }, []);

  return (
    <div className="united-page polymarket-page" ref={pageRef}>
      {/* The establishing shot stays in normal flow — its hero belongs to
          ProjectReveal's card-to-page FLIP, so it is never a member. */}
      <div className="united-content">
        <div className="united-text">
          <h1 className="united-title">A Few Ideas for Polymarket</h1>
          <p className="united-description">
            After graduating from college, I unexpectedly found myself balancing two pursuits: searching for my first product design role and developing prediction market trading algorithms. Along the way, I became a power user of the Polymarket iOS app. Here, I propose improvements to Polymarket’s existing trading experience, along with a few new features of my own.
          </p>
        </div>
        <div className="united-image">
          <PolymarketPlaceholder />
        </div>
      </div>

      <div className="pm-deck">
        {/* First in-flow child of the deck at height 0, so position: sticky
            pins it mid-viewport for the deck's whole run — see the CSS. */}
        <nav className="pm-toc" aria-label="Deck sections">
          {PM_TOC.map((entry, k) => (
            <button
              key={entry.label}
              type="button"
              className={`pm-toc-item${entry.chapter ? ' pm-toc-item--chapter' : ''}`}
              onClick={() => jumpTo(k)}
            >
              <span className="pm-toc-label">{entry.label}</span>
              <span className="pm-toc-dot" aria-hidden="true" />
            </button>
          ))}
        </nav>

        {/* Snap seats: one per member, --pm-step apart. These are what the
            scroll settles onto and what the TOC jumps to. */}
        {PM_TOC.map((entry, k) => (
          <div
            key={entry.label}
            className="pm-step"
            style={{ top: `calc(${k} * var(--pm-step))` }}
            aria-hidden="true"
          />
        ))}

        <div className="pm-stage">
          {/* comparison-section kept on every member so they ride
              ProjectReveal's open/close fades like everything else. */}
          <div className="pm-member comparison-section polymarket-part">
            <h2 className="polymarket-part-title" data-pm-t="0">
              Simplifying the Trader Experience
            </h2>
          </div>

          <div className="pm-member comparison-section polymarket-split polymarket-split--duo">
            <div className="polymarket-split-text">
              <h2 className="comparison-title" data-pm-t="0">Easier Binary Trading</h2>
              <p className="comparison-description" data-pm-t="1">
                <span className="polymarket-problem">Problem</span>: Changing the side of a binary event limit order isn't currently supported. The workaround is to use the order type dropdown to switch from Limit to Market, change the order side, and then use the dropdown again to switch back to Limit.
              </p>
              <p className="comparison-description" data-pm-t="1">
                <span className="polymarket-solution">Solution</span>: Both market and limit orders now support order switching through a simple toggle. This also eliminates the need for a toggle under the entry amount for market orders.
              </p>
            </div>
            <div className="polymarket-split-media" data-pm-t="2">
              <div className="mockup-comparison booking-edge-to-edge">
                <div className="mockup-container">
                  <div className="mockup-label">Today</div>
                  <div className="mockup-placeholder old-mockup">
                    <img
                      src="/polymarket-limit-old.png"
                      alt="Current Polymarket limit order screen"
                      className="comparison-image"
                      width={1409}
                      height={2927}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
                <div className="mockup-container">
                  <div className="mockup-label">New</div>
                  <div className="mockup-placeholder new-mockup">
                    <img
                      src="/polymarket-limit-new.png"
                      alt="Redesigned limit order screen with a Yes/No toggle"
                      className="comparison-image"
                      width={1371}
                      height={2847}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-stack">
            <h2 className="comparison-title" data-pm-t="0">Today, Market Titles Get Cutoff</h2>
            <p className="comparison-description" data-pm-t="1">
              <span className="polymarket-problem">Problem</span>: In markets like weather, this means that crucial information such as the date gets clipped in the header.
            </p>
            <div className="polymarket-slide" data-pm-t="2">
              <div className="mockup-placeholder old-mockup">
                <img
                  src="/polymarket-space-old.png"
                  alt="Current market and limit screens with titles clipped in the header"
                  className="comparison-image"
                  width={3480}
                  height={2190}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-stack">
            <h2 className="comparison-title" data-pm-t="0">Give Them Some Space</h2>
            <p className="comparison-description" data-pm-t="1">
              <span className="polymarket-solution">Solution</span>: Placing the titles outside of the main header allows for more horizontal space.
            </p>
            <div className="polymarket-slide" data-pm-t="2">
              <div className="mockup-placeholder new-mockup">
                <img
                  src="/polymarket-space-new.png"
                  alt="Redesigned market and limit screens with full titles below the header"
                  className="comparison-image"
                  width={3480}
                  height={2190}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-split polymarket-split--duo">
            <div className="polymarket-split-text">
              <h2 className="comparison-title" data-pm-t="0">Separating Order Controls</h2>
              <p className="comparison-description" data-pm-t="1">
                <span className="polymarket-problem">Problem</span>: Market and limit orders share a dropdown with the order book, despite serving different purposes; the former are order types, the latter is a pricing view.
              </p>
              <p className="comparison-description" data-pm-t="1">
                <span className="polymarket-solution">Solution</span>: Order methods are consolidated into a toggle, with the order book moved to a dedicated button beside it. Separating these controls removes the need for a second order book button beside Place Order on limit orders.
              </p>
            </div>
            <div className="polymarket-split-media" data-pm-t="2">
              <div className="mockup-comparison booking-edge-to-edge">
                <div className="mockup-container">
                  <div className="mockup-label">Today</div>
                  <div className="mockup-placeholder old-mockup">
                    <img
                      src="/polymarket-market-old.png"
                      alt="Current order type dropdown mixing order types with the order book"
                      className="comparison-image"
                      width={1362}
                      height={2838}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
                <div className="mockup-container">
                  <div className="mockup-label">New</div>
                  <div className="mockup-placeholder new-mockup">
                    <img
                      src="/polymarket-market-new.png"
                      alt="Redesigned header with a Market/Limit toggle and a separate order book button"
                      className="comparison-image"
                      width={1371}
                      height={2847}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-part">
            <h2 className="polymarket-part-title" data-pm-t="0">
              New Ideas
            </h2>
          </div>

          <div className="pm-member comparison-section polymarket-split polymarket-split--solo">
            <div className="polymarket-split-text">
              <h2 className="comparison-title" data-pm-t="0">Personalized Market Discovery</h2>
              <p className="comparison-description" data-pm-t="1">
                Polymarket offers many markets, but the ones you care about can get lost in the mix. The platform can recommend markets based on individual user behavior rather than relying on a one-size-fits-all feed.
              </p>
              <p className="comparison-description" data-pm-t="1">
                To address the cold-start problem, users could select topics they're interested in during onboarding. This enables relevant recommendations from day one.
              </p>
            </div>
            <div className="polymarket-split-media" data-pm-t="2">
              <div className="mockup-placeholder new-mockup">
                <img
                  src="/polymarket-interest.png"
                  alt="Onboarding screen for selecting markets of interest"
                  className="comparison-image"
                  width={1800}
                  height={3680}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-split polymarket-split--solo">
            <div className="polymarket-split-text">
              <h2 className="comparison-title" data-pm-t="0">Reorder Your Feed</h2>
              <p className="comparison-description" data-pm-t="1">
                While recommendations can be personalized algorithmically, allowing users to manually reorder categories on the home screen makes accessing their favorite markets even easier.
              </p>
            </div>
            <div className="polymarket-split-media" data-pm-t="2">
              <div className="mockup-placeholder new-mockup">
                <img
                  src="/polymarket-reorder.png"
                  alt="Dragging the Crypto category into a new spot in the feed"
                  className="comparison-image"
                  width={1800}
                  height={3680}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div className="pm-member comparison-section polymarket-split polymarket-split--solo">
            <div className="polymarket-split-text">
              <h2 className="comparison-title" data-pm-t="0">Leaderboard</h2>
              <p className="comparison-description" data-pm-t="1">
                People love knowing where they stand in the crowd. This leaderboard UI features dropdowns for category and time period, and a toggle for switching between profit and volume.
              </p>
              <p className="comparison-description" data-pm-t="1">
                Leaderboard could be the start of a much larger social section where you could view profiles, chat privately, or see what people have been predicting lately.
              </p>
            </div>
            <div className="polymarket-split-media" data-pm-t="2">
              <div className="mockup-placeholder new-mockup">
                <img
                  src="/polymarket-leaderboard.png"
                  alt="Leaderboard screen with category and time period filters"
                  className="comparison-image"
                  width={1800}
                  height={3680}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PolymarketPage;
